import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography,
} from '@mui/material';
import RangeList from './RangeList';
import { DAYS, rangesOverlap, withinOpenHours } from './weeklyHours';

// Edit one buyer's weekly schedule. "Same every day" edits one list of ranges
// and saves it on all 7 days; otherwise each day has its own list. A day with
// no ranges is off. There is no "no schedule" choice: all day = 07:00–02:00
// (only the page's open hours can be picked).

const NEW_RANGE = { start: '10:00', end: '22:00' };
const allSame = (weekly) => DAYS.every((d) => JSON.stringify(weekly[d.key] || []) === JSON.stringify(weekly['1'] || []));

export default function WeeklyHoursDialog({ buyer, onClose, onSave }) {
  const [same, setSame] = useState(true);
  const [days, setDays] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!buyer) return;
    const weekly = buyer.dispatchWeeklyHours;
    setSame(!weekly || allSame(weekly));
    setDays(Object.fromEntries(DAYS.map((d) => [d.key, weekly?.[d.key] || (weekly ? [] : [NEW_RANGE])])));
    setError(null);
  }, [buyer]);

  const setDay = (key, ranges) => setDays((prev) => ({ ...prev, [key]: ranges }));
  const setEveryDay = (ranges) => setDays(Object.fromEntries(DAYS.map((d) => [d.key, ranges])));

  const save = async () => {
    setSaving(true);
    const weekly = Object.fromEntries(DAYS.map((d) => [d.key, same ? days['1'] : days[d.key]]));
    const outside = DAYS.find((d) => weekly[d.key].some((r) => !withinOpenHours(r)));
    if (outside) {
      setError(`${same ? 'A' : `${outside.en}:`} time range runs into 02:00–07:00 — keep it inside 07:00–02:00.`);
      setSaving(false);
      return;
    }
    const clash = DAYS.find((d) => rangesOverlap(weekly[d.key]));
    if (clash) {
      setError(`${same ? 'The' : clash.en} time ranges overlap — make them not overlap, or remove one.`);
      setSaving(false);
      return;
    }
    setError(null);
    await onSave(weekly);
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(buyer)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Hours — {buyer?.label}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          30-minute steps. A range that ends before it starts runs past midnight (10:00–02:00 = until 2 am).
          A day with no range is off. Only 07:00–02:00 (when the page is open); all day = 07:00–02:00. Holidays: press Close for that day instead.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <FormControlLabel
          control={(
            <Checkbox
              id="weekly-same"
              checked={same}
              onChange={(e) => {
                setSame(e.target.checked);
                if (e.target.checked) setEveryDay(days['1'] || []);
              }}
            />
          )}
          label="Same every day"
        />
        {same ? (
          <RangeList idPrefix="every" ranges={days['1'] || []} onChange={setEveryDay} />
        ) : (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {DAYS.map((d) => (
              <Box key={d.key} sx={{ display: 'grid', gridTemplateColumns: '48px 1fr', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>{d.en}</Typography>
                <RangeList idPrefix={`day${d.key}`} ranges={days[d.key] || []} onChange={(r) => setDay(d.key, r)} />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save hours</Button>
      </DialogActions>
    </Dialog>
  );
}
